import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Switch,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Chip,
  Tooltip,
  Breadcrumbs,
  Link as MUILink,
  Divider,
  Stack,
  Alert,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  updateProfile,
  reloadUser,
  updatePassword,
  uploadProfileImage,
  deleteProfileImage,
  calculateProfileCompletion,
} from "../redux/slices/userSlice";
import { toast } from "react-toastify";
import { Logout } from "@mui/icons-material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonOutline from "@mui/icons-material/PersonOutline";
import Business from "@mui/icons-material/Business";
import AccountBalance from "@mui/icons-material/AccountBalance";
import LockReset from "@mui/icons-material/LockReset";
import NotificationsActive from "@mui/icons-material/NotificationsActive";
import CheckCircle from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import QrCode2Icon from '@mui/icons-material/QrCode2';
import QRCode from "react-qr-code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import { Link } from "react-router-dom";
// Import the ProfileImageUpload component
import ProfileImageUpload from "../Components/ProfileImageUpload";
import PageTitle from "../Components/PageTitle";
import QRLogo from "../Images/BizBridge-Logo-1.png"

export default function Settings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const { user, operations } = useSelector((state) => state.user);
  const profileCompletion = JSON.parse(
    localStorage.getItem("profileCompletion"),
  );
  // Access operations safely
  const profileUpdate = operations?.profileUpdate || {
    loading: false,
    success: false,
    error: null,
  };
  const passwordUpdate = operations?.passwordUpdate || {
    loading: false,
    success: false,
    error: null,
  };
  const imageUpload = operations?.imageUpload || {
    loading: false,
    success: false,
    error: null,
  };
  const imageDelete = operations?.imageDelete || {
    loading: false,
    success: false,
    error: null,
  };

  const [tabIndex, setTabIndex] = useState(0);
  // Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  // Password visibility state
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  // Profile image state
  const [profileImage, setProfileImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [openLogoutModal, setOpenLogoutModal] = useState(false);


   const handleOpenLogoutModal = () => {
    setOpenLogoutModal(true);
  };

  const handleCloseLogoutModal = () => setOpenLogoutModal(false);

  const handleLogout = () => {
    localStorage.clear();
    setOpenLogoutModal(false);
    window.location.reload("/login");
  };


  // Error states
  const [formErrors, setFormErrors] = useState({});

  const [localProfile, setLocalProfile] = useState({
    name: "",
    phone: "",
    email: "",
    businessName: "",
    businessAddress: "",
    gstNumber: "",
    bankName: "",
    accountName: "",
    accountNumber: "",
    upiId: "",
    ifscCode: "",
    branchName: "",
    branchAddress: "",
  });

  // notifications
  const [notifications, setNotifications] = useState({
    newOrders: true,
    payments: true,
    chatMessages: true,
    buyerRequests: true,
  });

  // Calculate profile completion when user data changes
  useEffect(() => {
    if (user) {
      dispatch(calculateProfileCompletion());
    }
  }, [user, dispatch]);

  // Show backend errors using toast
  useEffect(() => {
    if (profileUpdate.error) {
      toast.error(profileUpdate.error);
      dispatch({ type: "user/resetProfileUpdate" });
    }
    if (passwordUpdate.error) {
      toast.error(passwordUpdate.error);
      dispatch({ type: "user/resetPasswordUpdate" });
    }
    if (imageUpload.error) {
      toast.error(imageUpload.error);
      dispatch({ type: "user/resetImageUpload" });
    }
    if (imageDelete.error) {
      toast.error(imageDelete.error);
      dispatch({ type: "user/resetImageDelete" });
    }
  }, [
    profileUpdate.error,
    passwordUpdate.error,
    imageUpload.error,
    imageDelete.error,
    dispatch,
  ]);

  // Show backend errors using toast
  useEffect(() => {
    if (profileUpdate.error) {
      toast.error(profileUpdate.error);
      dispatch({ type: "user/resetProfileUpdate" });
    }
    if (passwordUpdate.error) {
      toast.error(passwordUpdate.error);
      dispatch({ type: "user/resetPasswordUpdate" });
    }
    if (imageUpload.error) {
      toast.error(imageUpload.error);
      dispatch({ type: "user/resetImageUpload" });
    }
    if (imageDelete.error) {
      toast.error(imageDelete.error);
      dispatch({ type: "user/resetImageDelete" });
    }
  }, [
    profileUpdate.error,
    passwordUpdate.error,
    imageUpload.error,
    imageDelete.error,
    dispatch,
  ]);

  // Show success messages using toast
  useEffect(() => {
    // Store previous success states to detect changes
    let mounted = true;

    // Only show toast and reset when success changes from false to true
    if (profileUpdate.success) {
      toast.success("Profile updated successfully!");
      // Reset after a small delay
      setTimeout(() => {
        if (mounted) {
          dispatch({ type: "user/resetProfileUpdate" });
        }
      }, 2000);
    }

    if (passwordUpdate.success) {
      toast.success("Password changed successfully!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => {
        if (mounted) {
          dispatch({ type: "user/resetPasswordUpdate" });
        }
      }, 2000);
    }

    if (imageUpload.success) {
      toast.success("Profile image uploaded successfully!");
      // Refresh user data after a delay
      setTimeout(() => {
        if (mounted) {
          dispatch(reloadUser());
          setImageFile(null);
          dispatch({ type: "user/resetImageUpload" });
        }
      }, 2000);
    }

    if (imageDelete.success) {
      toast.success("Profile image removed successfully!");
      setProfileImage(null);
      // Refresh user data after a delay
      setTimeout(() => {
        if (mounted) {
          dispatch(reloadUser());
          dispatch({ type: "user/resetImageDelete" });
        }
      }, 2000);
    }

    return () => {
      mounted = false;
    };
  }, [
    profileUpdate.success,
    passwordUpdate.success,
    imageUpload.success,
    imageDelete.success,
    dispatch,
  ]);

  // Load user data
  useEffect(() => {
    if (user) {
      setLocalProfile({
        name: user.name || "",
        phone: user.phone || "",
        email: user.email || "",
        businessName: user.businessName || "",
        businessAddress: user.businessAddress || "",
        gstNumber: user.gstNumber || "",
        bankName: user.bankDetails?.bankName || "",
        accountName: user.bankDetails?.accountName || "",
        accountNumber: user.bankDetails?.accountNumber || "",
        upiId: user.bankDetails?.upiId || "",
        ifscCode: user.bankDetails?.ifscCode || "",
        branchName: user.bankDetails?.branchName || "",
        branchAddress: user.bankDetails?.branchAddress || "",
      });

      // Set profile image if exists
      if (user.profileImage) {
        setProfileImage(user.profileImage);
      } else {
        setProfileImage(null);
      }
    }
  }, [user]);

  // Handle profile image change
  const handleImageChange = (file) => {
    setImageFile(file);

    // If file is null, it means remove image
    if (!file) {
      handleRemoveImage();
    }
  };

  // Handle image upload
  const handleImageUpload = async () => {
    if (!imageFile) return;

    const formData = new FormData();
    formData.append("profileImage", imageFile);

    try {
      await dispatch(uploadProfileImage(formData)).unwrap();
    } catch (error) {
      // Error handled in useEffect
    }
  };

  // Handle remove image
  const handleRemoveImage = async () => {
    try {
      await dispatch(deleteProfileImage()).unwrap();
    } catch (error) {
      // Error handled in useEffect
    }
  };

  // Auto upload image when imageFile changes
  useEffect(() => {
    if (imageFile) {
      handleImageUpload();
    }
  }, [imageFile]);

  const handleProfileChange = (field) => (e) =>
    setLocalProfile((prev) => ({ ...prev, [field]: e.target.value }));

  const handleNotificationChange = (field) => (e) => {
    const value = e.target.checked;
    setNotifications((prev) => ({ ...prev, [field]: value }));
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: localProfile.name,
        phone: localProfile.phone,
        email: localProfile.email,
        businessName: localProfile.businessName,
        businessAddress: localProfile.businessAddress,
        gstNumber: localProfile.gstNumber,
        bankDetails: {
          bankName: localProfile.bankName,
          accountName: localProfile.accountName,
          accountNumber: localProfile.accountNumber,
          upiId: localProfile.upiId,
          ifscCode: localProfile.ifscCode,
          branchName: localProfile.branchName,
          branchAddress: localProfile.branchAddress,
        },
      };

      const result = await dispatch(updateProfile(payload)).unwrap();
      if (result) {
        toast.success("Profile updated successfully!");
        dispatch(reloadUser());
      }
    } catch (error) {
      // toast.error("Failed to update profile. Please try again.");
    }
  };

  const handlePasswordChange = (field) => (e) => {
    setPasswordData((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear field-specific error
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Toggle password visibility
  const handleClickShowPassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validatePasswordForm = () => {
    const errors = {};

    if (!passwordData.currentPassword.trim()) {
      errors.currentPassword = "Current password is required";
    }

    if (!passwordData.newPassword.trim()) {
      errors.newPassword = "New password is required";
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = "Password must be at least 6 characters";
    }

    if (!passwordData.confirmPassword.trim()) {
      errors.confirmPassword = "Please confirm your password";
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    return errors;
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();

    const errors = validatePasswordForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      await dispatch(
        updatePassword({
          oldPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
          confirmPassword: passwordData.confirmPassword,
        }),
      ).unwrap();
    } catch (error) {
      toast.error("Failed to change password. Please try again.");
    }
  };

  const handleNotificationSave = (e) => {
    e.preventDefault();
    toast.success("Notification preferences updated!");
  };

  // Download QR Code function
  const handleDownloadQR = () => {
    const svg = document.querySelector("#seller-qr-code svg");
    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const qrImg = new Image();

    canvas.width = 400;
    canvas.height = 500;

    qrImg.onload = () => {
      // Gradient background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
      gradient.addColorStop(0, "#0b4064");
      gradient.addColorStop(1, "#0b4064");

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, 80);

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 80, canvas.width, canvas.height - 80);

      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 28px 'Arial', sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("BizBridge Pay", canvas.width / 2, 45);

      ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
      ctx.font = "14px 'Arial', sans-serif";
      ctx.fillText("Secure & Fast Payments", canvas.width / 2, 70);

      // Draw QR code with shadow
      ctx.shadowColor = "rgba(0, 0, 0, 0.1)";
      ctx.shadowBlur = 10;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 2;
      ctx.drawImage(qrImg, 75, 120, 250, 250);
      ctx.shadowBlur = 0;

      // Draw decorative border around QR
      ctx.strokeStyle = "#0b4064";
      ctx.lineWidth = 2;
      ctx.strokeRect(70, 115, 260, 260);

      // Load and draw logo
      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";

      logoImg.onload = () => {
        const qrCenterX = 75 + 250 / 2;
        const qrCenterY = 120 + 250 / 2;
        const logoSize = 60;

        // Draw white circle background for logo
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(qrCenterX, qrCenterY, logoSize / 2, 0, Math.PI * 2);
        ctx.fill();

        // Draw blue circle border
        ctx.strokeStyle = "#0b4064";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(qrCenterX, qrCenterY, logoSize / 2, 0, Math.PI * 2);
        ctx.stroke();

        // Draw logo image in center
        const logoDrawSize = 45;
        ctx.drawImage(
          logoImg,
          qrCenterX - logoDrawSize / 2.1,
          qrCenterY - logoDrawSize / 1.6,
          logoDrawSize,
          logoDrawSize
        );

        finishCanvas();
      };

      logoImg.onerror = () => {
        console.error("Logo failed to load, continuing without it");
        finishCanvas();
      };

      const finishCanvas = () => {
        const hasUPI = !!localProfile?.upiId;
        const sellerName = localProfile?.name || user?.name || "Dealer";

        // Add payment type label
        ctx.fillStyle = hasUPI ? "#28a745" : "#17a2b8";
        ctx.font = "bold 16px 'Arial', sans-serif";
        ctx.fillText(
          hasUPI ? "UPI PAYMENT" : "BANK TRANSFER",
          canvas.width / 2,
          400
        );

        // Add instruction
        ctx.fillStyle = "#333333";
        ctx.font = "18px 'Arial', sans-serif";
        ctx.fillText("Scan QR Code to Pay", canvas.width / 2, 430);

        // Add seller info
        ctx.fillStyle = "#666666";
        ctx.font = "16px 'Arial', sans-serif";
        ctx.fillText(`Pay to: ${sellerName}`, canvas.width / 2, 460);

        // Add UPI ID if available
        if (hasUPI) {
          ctx.fillStyle = "#0b4064";
          ctx.font = "14px 'Arial', sans-serif";
          ctx.fillText(
            `UPI ID: ${localProfile.upiId}`,
            canvas.width / 2,
            485
          );
        }

        const pngFile = canvas.toDataURL("image/png", 1.0);
        const downloadLink = document.createElement("a");
        downloadLink.download = `BizBridge-Pay-${sellerName.replace(
          /\s+/g,
          "-"
        )}.png`;
        downloadLink.href = pngFile;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      };

      logoImg.src = window.location.origin + "/QrImg.png";
    };

    qrImg.src =
      "data:image/svg+xml;base64," +
      btoa(unescape(encodeURIComponent(svgStr)));
  };

  const inputStyle = { mb: isMobile ? 2 : 0 };

  // Tab icons and labels
  const tabs = [
    { label: "Profile Completion", icon: <InfoIcon /> },
    { label: "Personal", icon: <PersonOutline /> },
    { label: "Business", icon: <Business /> },
    { label: "Bank", icon: <AccountBalance /> },
    { label: "Password", icon: <LockReset /> },
    { label: "QR", icon: <QrCode2Icon /> },
  ];

  // Get color based on completion percentage
  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return "#10b981"; // green
    if (percentage >= 50) return "#f59e0b"; // amber
    return "#ef4444"; // red
  };

  // Get completion status text
  const getCompletionStatus = (percentage) => {
    if (percentage >= 80) return "Complete";
    if (percentage >= 50) return "Moderate";
    return "Needs Attention";
  };

  return (
    <>
     <PageTitle
        title="Account Settings - BizBridge"
        description="Manage your personal profile, business details, bank information, password and notification preferences from your account settings."
        keywords="account settings, profile settings, business profile, bank details, change password, buyer dashboard"
        url="/buyer-dashboard/settings"
      />
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1400,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {/* Header Section with Profile Image */}
        <Card
          sx={{
            borderRadius: 3,
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            boxShadow: "0 8px 32px rgba(31, 38, 135, 0.08)",
            border: "1px solid rgba(255, 255, 255, 0.8)",
            overflow: "hidden",
          }}
        > 
        {/* Breadcrumb */}
                  <Breadcrumbs
                    aria-label="breadcrumb"
                    sx={{
                      width: "fit-content",
                      backgroundColor: "#fff",
                      ml: { xs: 1, sm: 1 },
                      mt: 1,
                      padding: "13px",
                      borderRadius: "12px",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                    }}
                  >
                    <MUILink
                      component={Link}
                      to="/buyer-dashboard"
                      sx={{ color: "inherit", textDecoration: "none" }}
                    >
                      Dashboard
                    </MUILink>
                    <Typography sx={{ color: "#0b4064", fontWeight: "bold" }}>
                      Settings
                    </Typography>
                  </Breadcrumbs>
        
                  <Divider sx={{ my: 2 }} />
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "center", md: "flex-start" },
                gap: 4,
              }}
            >
              {/* Profile Image Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minWidth: { xs: "100%", md: 200 },
                }}
              >
                <ProfileImageUpload
                  currentImage={profileImage?.url}
                  onImageChange={handleImageChange}
                  loading={imageUpload.loading || imageDelete.loading}
                  size={140}
                />
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="#0b4064"
                  sx={{ mt: 1 }}
                >
                  {user?.name || "Dealer Name"}
                </Typography>
                <Typography
                  variant="body2"
                  color="#0b4064"
                  sx={{ fontWeight: 500 }}
                >
                  {user?.email || ""}
                </Typography>
              </Box>

              {/* Header Text Section */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant={isMobile ? "h5" : "h4"}
                  fontWeight={800}
                  color="#131e32"
                  mb={1}
                  sx={{
                    background:
                      "linear-gradient(90deg, #131e32 0%, #2d3748 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Account Settings
                </Typography>
                <Typography
                  variant="body1"
                  color="#46639c"
                  sx={{ mb: 3, lineHeight: 1.6, fontWeight: 500 }}
                >
                  Manage your buyer profile, business information, bank
                  details, and notification preferences.
                </Typography>

                {/* Quick Stats */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 3,
                    flexWrap: "wrap",
                    mt: 3,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircle sx={{ color: "#10b981", fontSize: 20 }} />
                    <Typography
                      variant="body2"
                      color="#46639c"
                      fontWeight={500}
                    >
                      Profile {user?.name ? "Complete" : "Incomplete"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircle
                      sx={{
                        color: user?.businessName ? "#10b981" : "#ef4444",
                        fontSize: 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="#46639c"
                      fontWeight={500}
                    >
                      Business {user?.businessName ? "Verified" : "Pending"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircle
                      sx={{
                        color: user?.bankDetails?.accountNumber
                          ? "#10b981"
                          : "#ef4444",
                        fontSize: 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="#46639c"
                      fontWeight={500}
                    >
                      Bank{" "}
                      {user?.bankDetails?.accountNumber ? "Setup" : "Not Setup"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Main Content with Tabs */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
          }}
        >
          {/* Sidebar Tabs */}
          <Card
            sx={{
              width: { xs: "100%", md: 280 },
              borderRadius: 3,
              background: "white",
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              border: "1px solid rgba(0,0,0,0.04)",
              height: { xs: "auto", md: "fit-content" },
              overflow: "hidden",
            }}
          >
            <Tabs
              value={tabIndex}
              onChange={(e, val) => setTabIndex(val)}
              orientation={isMobile ? "horizontal" : "vertical"}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: { xs: "auto", md: 400 },
                "& .MuiTabs-flexContainer": {
                  gap: { xs: 1, md: 0 },
                },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  justifyContent: isMobile ? "center" : "flex-start",
                  alignItems: "center",
                  gap: 2,
                  py: { xs: 1.5, md: 2 },
                  px: 3,
                  minHeight: { xs: 48, md: 56 },
                  color: "#0b4064",
                  "&.Mui-selected": {
                    color: "#0b4064",
                    backgroundColor: "rgba(11, 64, 100, 0.08)",
                    borderRight: isMobile ? "none" : "3px solid #0b4064",
                    borderBottom: isMobile ? "3px solid #0b4064" : "none",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(11, 64, 100, 0.04)",
                  },
                },
                "& .MuiTabs-indicator": {
                  display: "none",
                },
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  icon={tab.icon}
                  iconPosition="start"
                  label={tab.label}
                  sx={{
                    borderRadius: 1,
                    mx: { xs: 0.5, md: 1 },
                    my: { xs: 0, md: 0.5 },
                  }}
                />
              ))}
            </Tabs>
          </Card>

          {/* Main Content Area */}
          <Box sx={{ flex: 1 }}>
            {tabIndex === 0 && (
              <Card sx={{
                  borderRadius: 3,
                  background: "white",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  border: "1px solid rgba(0,0,0,0.04)",
                  overflow: "hidden",
                }}>
                {/* Profile Completion Card */}
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      color="#131e32"
                    >
                      Profile Completion
                    </Typography>
                    <Tooltip title="Complete your profile to unlock all features">
                      <InfoIcon sx={{ color: "#64748b", fontSize: 18 }} />
                    </Tooltip>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h5"
                        fontWeight={800}
                        color={getCompletionColor(
                          profileCompletion?.percentage || 0,
                        )}
                      >
                        {profileCompletion?.percentage || 0}%
                      </Typography>
                      <Chip
                        label={getCompletionStatus(
                          profileCompletion?.percentage || 0,
                        )}
                        size="small"
                        sx={{
                          backgroundColor:
                            getCompletionColor(
                              profileCompletion?.percentage || 0,
                            ) + "20",
                          color: getCompletionColor(
                            profileCompletion?.percentage || 0,
                          ),
                          fontWeight: 600,
                        }}
                      />
                    </Box>

                    <LinearProgress
                      variant="determinate"
                      value={profileCompletion?.percentage || 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: "#e2e8f0",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: getCompletionColor(
                            profileCompletion?.percentage || 0,
                          ),
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>

                  {profileCompletion?.missingFields &&
                    profileCompletion.missingFields.length > 0 && (
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="#64748b"
                          mb={1}
                        >
                          Missing Information:
                        </Typography>

                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {profileCompletion.missingFields.map(
                            (field, index) => (
                              <Chip
                                key={index}
                                label={field}
                                size="small"
                                icon={<WarningAmberIcon fontSize="small" />}
                                sx={{
                                  backgroundColor: "#fee2e2",
                                  color: "#dc2626",
                                  fontWeight: 500,
                                }}
                              />
                            ),
                          )}
                        </Box>
                      </Box>
                    )}

                  {profileCompletion?.isProfileComplete && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 2,
                      }}
                    >
                      <DoneAllIcon sx={{ color: "#10b981", fontSize: 20 }} />
                      <Typography
                        variant="body2"
                        color="#10b981"
                        fontWeight={600}
                      >
                        Profile complete! You have access to all features.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
            {tabIndex === 1 && (
              <Card
                sx={{
                  borderRadius: 3,
                  background: "white",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  border: "1px solid rgba(0,0,0,0.04)",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color="#131e32"
                    mb={1}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <PersonOutline />
                    Personal Profile
                  </Typography>
                  <Typography
                    variant="body2"
                    color="#0b4064"
                    sx={{ mb: 4, fontWeight: 500 }}
                  >
                    Update your personal information and contact details
                  </Typography>

                  <Box component="form" onSubmit={handleProfileSave}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Full Name"
                          name="name"
                          value={localProfile.name}
                          onChange={handleProfileChange("name")}
                          fullWidth
                          size="medium"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Email"
                          name="email"
                          value={localProfile.email}
                          onChange={handleProfileChange("email")}
                          fullWidth
                          size="medium"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Phone"
                          name="phone"
                          value={localProfile.phone}
                          onChange={handleProfileChange("phone")}
                          fullWidth
                          size="medium"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Button
                      type="submit"
                      variant="contained"
                      sx={{
                        mt: 4,
                        px: 5,
                        py: 1.5,
                        fontWeight: 700,
                        borderRadius: 2,
                        background:
                          "linear-gradient(90deg, #0b4064 0%, #0b4064 100%)",
                        textTransform: "none",
                        fontSize: "1rem",
                        boxShadow: "0 4px 12px rgba(11, 64, 100, 0.3)",
                        "&:hover": {
                          background:
                            "linear-gradient(90deg, #0b4064 0%, #0b4064 100%)",
                          boxShadow: "0 6px 16px rgba(11, 64, 100, 0.4)",
                        },
                      }}
                      disabled={profileUpdate.loading}
                      startIcon={profileUpdate.loading ? null : <CheckCircle />}
                    >
                      {profileUpdate.loading ? (
                        <>
                          <CircularProgress
                            size={20}
                            sx={{ mr: 1, color: "white" }}
                          />
                          Saving...
                        </>
                      ) : (
                        "Save Personal Details"
                      )}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {tabIndex === 2 && (
              <Card
                sx={{
                  borderRadius: 3,
                  background: "white",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  border: "1px solid rgba(0,0,0,0.04)",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color="#131e32"
                    mb={1}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Business />
                    Business Profile
                  </Typography>
                  <Typography
                    variant="body2"
                    color="#0b4064"
                    sx={{ mb: 4, fontWeight: 500 }}
                  >
                    Manage your business information and registration details
                  </Typography>

                  <Box component="form" onSubmit={handleProfileSave}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Business Name"
                          name="businessName"
                          value={localProfile.businessName}
                          onChange={handleProfileChange("businessName")}
                          fullWidth
                          size="medium"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="GST Number"
                          name="gstNumber"
                          value={localProfile.gstNumber}
                          onChange={handleProfileChange("gstNumber")}
                          fullWidth
                          size="medium"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Business Address"
                          name="businessAddress"
                          value={localProfile.businessAddress}
                          onChange={handleProfileChange("businessAddress")}
                          fullWidth
                          size="medium"
                          multiline
                          minRows={3}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Button
                      type="submit"
                      variant="contained"
                      sx={{
                        mt: 4,
                        px: 5,
                        py: 1.5,
                        fontWeight: 700,
                        borderRadius: 2,
                        background:
                          "linear-gradient(90deg, #0b4064 0%, #0b4064 100%)",
                        textTransform: "none",
                        fontSize: "1rem",
                        boxShadow: "0 4px 12px rgba(11, 64, 100, 0.3)",
                        "&:hover": {
                          background:
                            "linear-gradient(90deg, #0b4064 0%, #0b4064 100%)",
                          boxShadow: "0 6px 16px rgba(11, 64, 100, 0.4)",
                        },
                      }}
                      disabled={profileUpdate.loading}
                      startIcon={profileUpdate.loading ? null : <CheckCircle />}
                    >
                      {profileUpdate.loading ? (
                        <>
                          <CircularProgress
                            size={20}
                            sx={{ mr: 1, color: "white" }}
                          />
                          Saving...
                        </>
                      ) : (
                        "Save Business Details"
                      )}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {tabIndex === 3 && (
              <Card
                sx={{
                  borderRadius: 3,
                  background: "white",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  border: "1px solid rgba(0,0,0,0.04)",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color="#131e32"
                    mb={1}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <AccountBalance />
                    Bank Details
                  </Typography>
                  <Typography
                    variant="body2"
                    color="#0b4064"
                    sx={{ mb: 4, fontWeight: 500 }}
                  >
                    Update your bank account information for payments
                  </Typography>

                  <Box component="form" onSubmit={handleProfileSave}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Bank Name"
                          name="bankName"
                          value={localProfile.bankName}
                          onChange={handleProfileChange("bankName")}
                          fullWidth
                          size="medium"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Account Holder Name"
                          name="accountName"
                          value={localProfile.accountName}
                          onChange={handleProfileChange("accountName")}
                          fullWidth
                          size="medium"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Account Number"
                          name="accountNumber"
                          value={localProfile.accountNumber}
                          onChange={handleProfileChange("accountNumber")}
                          fullWidth
                          size="medium"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="UPI ID"
                          name="upiId"
                          value={localProfile.upiId}
                          onChange={handleProfileChange("upiId")}
                          fullWidth
                          size="medium"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="IFSC Code"
                          name="ifscCode"
                          value={localProfile.ifscCode}
                          onChange={handleProfileChange("ifscCode")}
                          fullWidth
                          size="medium"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Branch Name"
                          name="branchName"
                          value={localProfile.branchName}
                          onChange={handleProfileChange("branchName")}
                          fullWidth
                          size="medium"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Branch Address"
                          name="branchAddress"
                          value={localProfile.branchAddress}
                          onChange={handleProfileChange("branchAddress")}
                          fullWidth
                          size="medium"
                          multiline
                          minRows={2}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Button
                      type="submit"
                      variant="contained"
                      sx={{
                        mt: 4,
                        px: 5,
                        py: 1.5,
                        fontWeight: 700,
                        borderRadius: 2,
                        background:
                          "linear-gradient(90deg, #0b4064 0%, #0b4064 100%)",
                        textTransform: "none",
                        fontSize: "1rem",
                        boxShadow: "0 4px 12px rgba(11, 64, 100, 0.3)",
                        "&:hover": {
                          background:
                            "linear-gradient(90deg, #0b4064 0%, #0b4064 100%)",
                          boxShadow: "0 6px 16px rgba(11, 64, 100, 0.4)",
                        },
                      }}
                      disabled={profileUpdate.loading}
                      startIcon={profileUpdate.loading ? null : <CheckCircle />}
                    >
                      {profileUpdate.loading ? (
                        <>
                          <CircularProgress
                            size={20}
                            sx={{ mr: 1, color: "white" }}
                          />
                          Saving...
                        </>
                      ) : (
                        "Save Bank Details"
                      )}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {tabIndex === 4 && (
              <Card
                sx={{
                  borderRadius: 3,
                  background: "white",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  border: "1px solid rgba(0,0,0,0.04)",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color="#131e32"
                    mb={1}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <LockReset />
                    Change Password
                  </Typography>
                  <Typography
                    variant="body2"
                    color="#0b4064"
                    sx={{ mb: 4, fontWeight: 500 }}
                  >
                    Update your password to keep your account secure
                  </Typography>

                  <Box component="form" onSubmit={handlePasswordSave}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          label="Current Password"
                          type={showPassword.current ? "text" : "password"}
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange("currentPassword")}
                          fullWidth
                          size="medium"
                          error={!!formErrors.currentPassword}
                          helperText={formErrors.currentPassword}
                          disabled={passwordUpdate.loading}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() =>
                                    handleClickShowPassword("current")
                                  }
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                  size="small"
                                >
                                  {showPassword.current ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="New Password"
                          type={showPassword.new ? "text" : "password"}
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange("newPassword")}
                          fullWidth
                          size="medium"
                          error={!!formErrors.newPassword}
                          helperText={formErrors.newPassword}
                          disabled={passwordUpdate.loading}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => handleClickShowPassword("new")}
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                  size="small"
                                >
                                  {showPassword.new ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Confirm New Password"
                          type={showPassword.confirm ? "text" : "password"}
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange("confirmPassword")}
                          fullWidth
                          size="medium"
                          error={!!formErrors.confirmPassword}
                          helperText={formErrors.confirmPassword}
                          disabled={passwordUpdate.loading}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#0b4064",
                              },
                            },
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() =>
                                    handleClickShowPassword("confirm")
                                  }
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                  size="small"
                                >
                                  {showPassword.confirm ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Button
                      type="submit"
                      variant="contained"
                      sx={{
                        mt: 4,
                        px: 5,
                        py: 1.5,
                        fontWeight: 700,
                        borderRadius: 2,
                        background:
                          "linear-gradient(90deg, #0b4064 0%, #0b4064 100%)",
                        textTransform: "none",
                        fontSize: "1rem",
                        boxShadow: "0 4px 12px rgba(11, 64, 100, 0.3)",
                        "&:hover": {
                          background:
                            "linear-gradient(90deg, #0b4064 0%, #0b4064 100%)",
                          boxShadow: "0 6px 16px rgba(11, 64, 100, 0.4)",
                        },
                      }}
                      disabled={passwordUpdate.loading}
                      startIcon={
                        passwordUpdate.loading ? null : <CheckCircle />
                      }
                    >
                      {passwordUpdate.loading ? (
                        <>
                          <CircularProgress
                            size={20}
                            sx={{ mr: 1, color: "white" }}
                          />
                          Updating...
                        </>
                      ) : (
                        "Change Password"
                      )}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {tabIndex === 5 && (
              <Card
                sx={{
                  borderRadius: 3,
                  background: "white",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  border: "1px solid rgba(0,0,0,0.04)",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color="#131e32"
                    mb={1}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <QrCode2Icon />
                    Dealer QR Code
                  </Typography>
                  <Typography
                    variant="body2"
                    color="#0b4064"
                    sx={{ mb: 3, fontWeight: 500 }}
                  >
                    Your payment QR code for dealers to scan
                  </Typography>

                  {/* Direct QR Code Display */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 2,
                    }}
                  >
                    {/* Check if payment details exist */}
                    {localProfile?.upiId ||
                    localProfile?.accountNumber ? (
                      <>
                        {/* QR Code with Logo */}
                        <Box
                          id="buyer-qr-code"
                          sx={{
                            position: "relative",
                            display: "inline-block",
                            mb: 3,
                          }}
                        >
                          <Box
                            sx={{
                              p: 3,
                              border: "2px solid",
                              borderColor: "primary.light",
                              borderRadius: 3,
                              bgcolor: "#fff",
                              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                              position: "relative",
                            }}
                          >
                            <QRCode
                              value={
                                localProfile?.upiId
                                  ? `upi://pay?pa=${
                                      localProfile.upiId
                                    }&pn=${
                                      localProfile?.name ||
                                      user?.name ||
                                      "Dealer"
                                    }&am=&cu=INR&tn=Payment%20via%20BizBridge`
                                  : `BizBridge Payment:
Dealer: ${localProfile?.name || user?.name || "Dealer"}
Bank: ${localProfile?.bankName || "N/A"}
Account: ${localProfile?.accountNumber || "N/A"}
IFSC: ${localProfile?.ifscCode || "N/A"}`
                              }
                              size={250}
                              bgColor="#ffffff"
                              fgColor="#000000"
                              level="H"
                            />
                            {/* Logo overlay */}
                            <Box
                              sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                width: 60,
                                height: 60,
                                borderRadius: "50%",
                                bgcolor: "white",
                                border: "3px solid",
                                borderColor: "primary.main",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                overflow: "hidden",
                              }}
                            >
                              <img
                                src={QRLogo}
                                alt="BizBridge Logo"
                                style={{
                                  width: "42px",
                                  height: "42px",
                                  objectFit: "contain",
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>

                        {/* Payment Info */}
                        <Box sx={{ mb: 2, textAlign: "center" }}>
                          <Chip
                            icon={
                              localProfile?.upiId ? (
                                <QrCode2Icon />
                              ) : (
                                <AccountBalance />
                              )
                            }
                            label={
                              localProfile?.upiId
                                ? "UPI Payment"
                                : "Bank Transfer"
                            }
                            color={
                              localProfile?.upiId
                                ? "success"
                                : "info"
                            }
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.9rem",
                              py: 1,
                              px: 2,
                              mb: 2,
                            }}
                          />
                          <Typography
                            variant="body1"
                            fontWeight={600}
                            color="primary"
                          >
                            {localProfile?.upiId
                              ? "Scan with PhonePe, GPay, or any UPI app"
                              : "Scan to view bank transfer details"}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            mt={1}
                          >
                            Pay to:{" "}
                            <strong>
                              {localProfile?.name || user?.name || "Dealer"}
                            </strong>
                          </Typography>
                        </Box>

                        {/* UPI ID Display */}
                        {localProfile?.upiId && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                              mb: 2,
                              p: 2,
                              bgcolor: "grey.50",
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "grey.200",
                              maxWidth: 400,
                            }}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              UPI ID:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                bgcolor: "white",
                                px: 2,
                                py: 0.5,
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "grey.300",
                              }}
                            >
                              {localProfile.upiId}
                            </Typography>
                            <IconButton
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  localProfile.upiId
                                );
                                toast.success("UPI ID copied to clipboard!");
                              }}
                              size="small"
                              sx={{
                                bgcolor: "primary.main",
                                color: "white",
                                "&:hover": {
                                  bgcolor: "primary.dark",
                                },
                              }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}

                        {/* Bank Details if no UPI */}
                        {!localProfile?.upiId &&
                          localProfile?.accountNumber && (
                            <Alert
                              severity="info"
                              sx={{
                                textAlign: "left",
                                mb: 2,
                                borderRadius: 2,
                                maxWidth: 500,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                gutterBottom
                              >
                                Bank Transfer Details:
                              </Typography>
                              <Stack spacing={0.5}>
                                {localProfile?.bankName && (
                                  <Typography variant="body2">
                                    <strong>Bank:</strong>{" "}
                                    {localProfile.bankName}
                                  </Typography>
                                )}
                                {localProfile?.accountNumber && (
                                  <Typography variant="body2">
                                    <strong>Account:</strong>{" "}
                                    {localProfile.accountNumber}
                                  </Typography>
                                )}
                                {localProfile?.accountName && (
                                  <Typography variant="body2">
                                    <strong>Name:</strong>{" "}
                                    {localProfile.accountName}
                                  </Typography>
                                )}
                                {localProfile?.ifscCode && (
                                  <Typography variant="body2">
                                    <strong>IFSC:</strong>{" "}
                                    {localProfile.ifscCode}
                                  </Typography>
                                )}
                              </Stack>
                            </Alert>
                          )}

                        {/* Download Button */}
                        <Button
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={handleDownloadQR}
                          sx={{
                            mt: 2,
                            px: 4,
                            py: 1.5,
                            fontWeight: 700,
                            borderRadius: 2,
                            background:
                              "linear-gradient(90deg, #0b4064 0%, #0b4064 100%)",
                            boxShadow: "0 4px 12px rgba(11, 64, 100, 0.3)",
                            "&:hover": {
                              background:
                                "linear-gradient(90deg, #0b4064 0%, #0b4064 100%)",
                              boxShadow: "0 6px 16px rgba(11, 64, 100, 0.4)",
                            },
                          }}
                        >
                          Download QR Code
                        </Button>
                      </>
                    ) : (
                      /* No Payment Details Warning */
                      <Card
                        sx={{
                          mt: 2,
                          p: 3,
                          borderRadius: 2,
                          backgroundColor: "#fff3cd",
                          border: "1px solid #ffc107",
                          maxWidth: 500,
                        }}
                      >
                        <Typography
                          variant="h6"
                          color="#856404"
                          sx={{ mb: 1, fontWeight: 600 }}
                        >
                          ⚠️ No Payment Details Found
                        </Typography>
                        <Typography variant="body2" color="#856404">
                          Please add your UPI ID or bank details in the "Bank
                          Details" tab to generate your QR code.
                        </Typography>
                      </Card>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>

        {/* Notification Settings */}
        <Card
          sx={{
            borderRadius: 3,
            background: "white",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid rgba(0,0,0,0.04)",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Typography
              variant="h5"
              fontWeight={700}
              color="#131e32"
              mb={1}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <NotificationsActive />
              Notification Settings
            </Typography>
            <Typography
              variant="body2"
              color="#0b4064"
              sx={{ mb: 4, fontWeight: 500 }}
            >
              Manage how you receive notifications as a buyer)
            </Typography>

            <Box
              component="form"
              onSubmit={handleNotificationSave}
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              {[
                {
                  label: "New Orders",
                  description: "Receive notifications for new orders.",
                  name: "newOrders",
                },
                {
                  label: "Payments",
                  description: "Get notified about payment updates.",
                  name: "payments",
                },
                {
                  label: "Chat Messages",
                  description: "Receive notifications for new chat messages.",
                  name: "chatMessages",
                },
                {
                  label: "Buyer Requests",
                  description: "Get notified about buyer requests.",
                  name: "buyerRequests",
                },
              ].map(({ label, description, name }) => (
                <Card
                  key={name}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: "#f8fafc",
                    border: "1px solid rgba(0,0,0,0.04)",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: "#f1f5f9",
                      borderColor: "#0b4064",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                        {label}
                      </Typography>
                      <Typography variant="body2" color="#0b4064">
                        {description}
                      </Typography>
                    </Box>
                    <Switch
                      checked={notifications[name]}
                      disabled
                      onChange={handleNotificationChange(name)}
                      color="primary"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#0b4064",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#0b4064",
                          },
                      }}
                    />
                  </Box>
                </Card>
              ))}
              <Button
                type="submit"
                variant="contained"
                disabled
                sx={{
                  alignSelf: "flex-start",
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: 2,
                  background:
                    "linear-gradient(90deg, #0b4064 0%, #0b4064 100%)",
                  textTransform: "none",
                  fontSize: "1rem",
                  boxShadow: "0 4px 12px rgba(11, 64, 100, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, #0b4064 0%, #0b4064 100%)",
                    boxShadow: "0 6px 16px rgba(11, 64, 100, 0.4)",
                  },
                }}
                startIcon={<CheckCircle />}
              >
                Save Notifications
              </Button>
            </Box>
          </CardContent>
        </Card>

<Card
  sx={{
    borderRadius: 3,
    background: "white",
    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
    border: "1px solid rgba(0,0,0,0.04)",
    overflow: "hidden",
  }}
>
  <CardContent sx={{ p: { xs: 3, md: 5 } }}>
    <Typography
      variant="h5"
      fontWeight={700}
      color="#131e32"
      mb={1}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Logout />
      Buyer Logout
    </Typography>

    <Typography
      variant="body2"
      color="#0b4064"
      sx={{ mb: 4, fontWeight: 500 }}
    >
      Securely sign out from your buyer account
    </Typography>

    <MenuItem
      onClick={handleOpenLogoutModal}
      sx={{
        py: 1.5,
        px: 2,
        fontSize: 14,
        color: "#dc2626",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        borderRadius: 2,
        "&:hover": {
          backgroundColor: "#fef2f2",
        },
      }}
    >
      <Logout sx={{ fontSize: 20, color: "#dc2626" }} />
      Confirm Logout
    </MenuItem>
  </CardContent>
</Card>


         <Dialog open={openLogoutModal} onClose={handleCloseLogoutModal}>
                  <DialogTitle>Logout</DialogTitle>
                  <DialogContent>
                    <Typography>Are you sure you want to logout?</Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleCloseLogoutModal}>Cancel</Button>
                    <Button color="primary" variant="contained" onClick={handleLogout}>
                      Logout
                    </Button>
                  </DialogActions>
                </Dialog>
      </Box>
    </Box>
    </>
  );
}